import { OcrError } from './errors';
import type { Provider } from './storage';

export interface Transcription {
  title: string;
  tags: string[];
  body: string;
}

const GEMINI_MODEL = 'gemini-2.5-flash';
const GEMINI_ENDPOINT = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

const PROMPT = `You are transcribing handwritten notes from a photograph into Markdown.

Produce:
- title: a short (<=60 char) title summarising the content.
- tags: 2-6 lowercase tags reflecting the topics of the page.
- body: a clean Markdown transcription of the page. Preserve structure exactly:
  * Paragraphs: separate every paragraph break with a BLANK LINE — that is, two consecutive newlines ("\\n\\n") in the JSON string. A single newline is not a paragraph break in Markdown, so you must use two.
  * Headings: use #, ##, ### for lines that appear as section titles (underlined, larger, centred, or clearly set off).
  * Lists: use "- " for bulleted items and "1. ", "2. " for numbered items. One item per line.
  * Emphasis: use **bold** and *italic* only where the writer clearly indicates it (underlining, circles, heavy weight).
  * Line breaks inside a paragraph (handwriting wrap): join with a single space — do not preserve the visual line wrapping of the page.
  Do not wrap the response in code fences. Do not add commentary, preamble, or explanation outside the JSON.

If the image is illegible or blank, return title "Illegible", tags ["illegible"], and a body briefly describing what you see (e.g. "The image is blank." or "The handwriting is too unclear to transcribe.").`;

const RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    title: { type: 'string' },
    tags: { type: 'array', items: { type: 'string' } },
    body: { type: 'string' },
  },
  required: ['title', 'tags', 'body'],
};

export interface TranscribeOptions {
  maxAttempts?: number;
  baseRetryMs?: number;
}

export async function transcribe(
  imageBase64: string,
  mimeType: string,
  apiKey: string,
  provider: Provider = 'gemini',
  opts: TranscribeOptions = {},
): Promise<Transcription> {
  switch (provider) {
    case 'gemini':
      return transcribeWithGemini(imageBase64, mimeType, apiKey, opts);
  }
}

const DEFAULT_MAX_ATTEMPTS = 4;
const DEFAULT_BASE_RETRY_MS = 2000;

async function transcribeWithGemini(
  imageBase64: string,
  mimeType: string,
  apiKey: string,
  opts: TranscribeOptions,
): Promise<Transcription> {
  if (!apiKey) {
    throw new OcrError('invalid_key', 'Gemini API key is not configured.');
  }

  const maxAttempts = opts.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const baseRetryMs = opts.baseRetryMs ?? DEFAULT_BASE_RETRY_MS;

  const url = `${GEMINI_ENDPOINT}?key=${encodeURIComponent(apiKey)}`;
  const body = JSON.stringify({
    contents: [
      {
        parts: [
          { text: PROMPT },
          { inline_data: { mime_type: mimeType, data: imageBase64 } },
        ],
      },
    ],
    generationConfig: {
      response_mime_type: 'application/json',
      response_schema: RESPONSE_SCHEMA,
      temperature: 0.2,
    },
  });

  const response = await fetchWithRetry(url, body, maxAttempts, baseRetryMs);
  if (!response.ok) {
    await handleTerminalError(response);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (err) {
    throw new OcrError('parse_failed', 'Gemini response was not valid JSON.', String(err));
  }

  const text = extractText(payload);
  if (!text) {
    throw new OcrError(
      'parse_failed',
      'Gemini response did not contain expected text.',
      JSON.stringify(payload).slice(0, 200),
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new OcrError(
      'parse_failed',
      'Gemini response could not be parsed as transcription JSON.',
      text.slice(0, 200),
    );
  }

  if (!isTranscription(parsed)) {
    throw new OcrError(
      'parse_failed',
      'Gemini response missing title, tags, or body.',
      JSON.stringify(parsed).slice(0, 200),
    );
  }

  if (parsed.title.trim().toLowerCase() === 'illegible') {
    throw new OcrError(
      'illegible',
      `The handwriting could not be transcribed: ${parsed.body.trim()}`,
      parsed.body,
    );
  }

  return parsed;
}

function extractText(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const candidates = (payload as { candidates?: unknown }).candidates;
  if (!Array.isArray(candidates) || candidates.length === 0) return null;
  const first = candidates[0];
  if (typeof first !== 'object' || first === null) return null;
  const content = (first as { content?: unknown }).content;
  if (typeof content !== 'object' || content === null) return null;
  const parts = (content as { parts?: unknown }).parts;
  if (!Array.isArray(parts) || parts.length === 0) return null;
  const texts = parts
    .map((p) => (typeof p === 'object' && p !== null ? (p as { text?: unknown }).text : null))
    .filter((t): t is string => typeof t === 'string');
  return texts.length > 0 ? texts.join('') : null;
}

function isTranscription(value: unknown): value is Transcription {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.title === 'string' &&
    Array.isArray(v.tags) &&
    v.tags.every((t) => typeof t === 'string') &&
    typeof v.body === 'string'
  );
}

async function fetchWithRetry(
  url: string,
  body: string,
  maxAttempts: number,
  baseRetryMs: number,
): Promise<Response> {
  let lastResponse: Response | null = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
    } catch (err) {
      if (attempt === maxAttempts) {
        throw new OcrError(
          'network',
          'Could not reach the vision model after several tries. Check your connection.',
          String(err),
        );
      }
      if (baseRetryMs > 0) await sleep(baseRetryMs * 2 ** (attempt - 1));
      continue;
    }

    lastResponse = response;
    if (response.ok) return response;
    if (!isTransient(response.status) || attempt === maxAttempts) return response;
    if (baseRetryMs > 0) await sleep(baseRetryMs * 2 ** (attempt - 1));
  }
  return lastResponse!;
}

function isTransient(status: number): boolean {
  // 503: model overloaded (the one the user hit); 502/504: upstream flake;
  // 429: often transient on free tier even when it looks like a quota.
  return status === 429 || status === 502 || status === 503 || status === 504;
}

async function handleTerminalError(response: Response): Promise<never> {
  if (response.status === 400 || response.status === 401 || response.status === 403) {
    throw new OcrError('invalid_key', 'Gemini API key is invalid or lacks permission.');
  }
  if (response.status === 429) {
    throw new OcrError(
      'quota_exceeded',
      'Gemini is rate-limiting us even after retries. Wait a minute and try again.',
    );
  }
  if (response.status === 503) {
    throw new OcrError(
      'unknown',
      'The Gemini model is overloaded right now. Try again in a moment.',
    );
  }
  const detail = await safeText(response);
  throw new OcrError(
    'unknown',
    `Gemini returned HTTP ${response.status}. ${summariseGeminiError(detail)}`,
    detail,
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function safeText(response: Response): Promise<string> {
  try {
    return (await response.text()).slice(0, 500);
  } catch {
    return '';
  }
}

function summariseGeminiError(raw: string): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw) as { error?: { message?: string } };
    const msg = parsed.error?.message;
    if (typeof msg === 'string' && msg.length > 0) {
      return msg.length > 200 ? `${msg.slice(0, 200)}…` : msg;
    }
  } catch {
    /* fall through */
  }
  return raw.slice(0, 200);
}
